/**
 * DecisionFlow — MarketShareManager
 * ui/src/components/tenant-admin/MarketShareManager.jsx
 *
 * Domain: §P4-14 Market Share Quotas
 * Nav route: /admin/market-share
 * API:
 *   GET  /api/v1/market-share-quotas?fiscal_period_id={id}
 *   POST /api/v1/market-share-quotas
 *   POST /api/v1/market-share-quotas/{id}/approve
 * Feature gate: Enterprise — market_share
 *   403 FEATURE_UNAVAILABLE → GateStatusBanner.
 * territory_id scoped (no plan_component_id).
 * BU-null for Tier 2 only.
 * State derived from approved_by / submitted_at — no status column.
 * Path: ui/src/components/tenant-admin/MarketShareManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  territory_id:        '',
  target_share_pct:    '',
  payout_rate_pct:     '',
  effective_date:      '',
  expiration_date:     '',
};

// ---------------------------------------------------------------------------
// MarketShareManager
// ---------------------------------------------------------------------------

export default function MarketShareManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.market_share')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }
  const fpId = period?.fiscal_period_id ?? fiscalPeriodId ?? null;

  const [gated, setGated]                   = useState(false);
  const [items, setItems]                   = useState([]);
  const [listLoading, setListLoading]       = useState(false);
  const [listError, setListError]           = useState('');
  const [selectedItem, setSelectedItem]     = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [saving, setSaving]                 = useState(false);
  const [formError, setFormError]           = useState('');
  const [actionLoading, setActionLoading]   = useState(false);
  const [actionError, setActionError]       = useState('');
  const [toast, setToast]                   = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // ---------------------------------------------------------------------------
  // Load — enterprise gate check first
  // ---------------------------------------------------------------------------

  const loadItems = useCallback(async () => {
    if (!user) return;
    setListLoading(true);
    setListError('');
    setGated(false);
    try {
      const url = fpId
        ? `/api/v1/market-share-quotas?fiscal_period_id=${fpId}`
        : '/api/v1/market-share-quotas';
      const data = await apiGet(url);
      setItems(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'FEATURE_UNAVAILABLE') {
        setGated(true);
      } else {
        setListError(getErrorMessage(error, 'Unable to load market share quotas.'));
      }
    } finally {
      setListLoading(false);
    }
  }, [user, fpId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ---------------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------------

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.territory_id.trim() || !form.target_share_pct || !form.payout_rate_pct) {
      setFormError('Territory ID, target share, and payout rate are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await apiPost('/api/v1/market-share-quotas', {
        territory_id:      form.territory_id.trim(),
        fiscal_period_id:  fpId,
        target_share_pct:  parseFloat(form.target_share_pct),
        payout_rate_pct:   parseFloat(form.payout_rate_pct),
        effective_date:    form.effective_date || null,
        expiration_date:   form.expiration_date || null,
        // business_unit_id omitted (BU-null for Tier 2)
      });
      setShowCreateForm(false);
      setForm(EMPTY_FORM);
      showToast('Market share quota created.');
      await loadItems();
    } catch (error) {
      setFormError(getErrorMessage(error, 'Failed to create market share quota.'));
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Submit / Approve
  // ---------------------------------------------------------------------------

  async function handleSubmit() {
    if (!selectedItem) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/market-share-quotas/${selectedItem.market_share_quota_id}/submit`);
      showToast('Submitted for approval.');
      await loadItems();
      setSelectedItem((prev) => ({ ...prev, submitted_at: new Date().toISOString() }));
    } catch (error) {
      setActionError(getErrorMessage(error, 'Failed to submit.'));
    } finally {
      setActionLoading(false);
    }
  }

  async function handleApprove() {
    if (!selectedItem) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/market-share-quotas/${selectedItem.market_share_quota_id}/approve`);
      showToast('Market share quota approved.');
      await loadItems();
      setSelectedItem((prev) => ({ ...prev, approved_by: user?.user_id || user?.id }));
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_NOT_ALLOWED') {
        setActionError('Self-approval is not permitted (NN-10).');
      } else {
        setActionError(getErrorMessage(error, 'Failed to approve.'));
      }
    } finally {
      setActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) {
    return (
      <section className="flex flex-col gap-4">
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  if (gated) {
    return (
      <section className="flex flex-col gap-4">
        <GateStatusBanner feature="market_share" />
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Detail view
  // ---------------------------------------------------------------------------

  if (selectedItem) {
    const state = deriveState(selectedItem);
    return (
      <section className="flex flex-col gap-4">
        <button type="button" onClick={() => { setSelectedItem(null); setActionError(''); }} className={btnSecondary + ' w-fit'}>
          ← Back to list
        </button>

        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
              Territory {selectedItem.territory_id}
            </h2>
            <StatusPill status={state} />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Target Share</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.target_share_pct != null ? `${selectedItem.target_share_pct}%` : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Payout Rate</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.payout_rate_pct != null ? `${selectedItem.payout_rate_pct}%` : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{selectedItem.effective_date ?? '—'}</p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Expires</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{selectedItem.expiration_date ?? '—'}</p>
            </div>
          </div>
          {actionError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
              {actionError}
            </div>
          )}
        </div>

        {!readOnly && (
          <ApprovalPanel
            variant="market_share_quota"
            currentState={state}
            submittedBy={selectedItem.submitted_by}
            currentUserId={currentUserId}
            onSubmit={state === 'draft' ? handleSubmit : null}
            onApprove={state === 'pending_approval' ? handleApprove : null}
            onReject={null}
            approvedBy={selectedItem.approved_by}
            approvedAt={selectedItem.approved_at}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // List view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">Market Share</h1>

      {!fpId && (
        <div className="rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
          No active fiscal period. Market share quotas require a period.
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Market Share Quotas</h2>
        {!readOnly && (
          <button
            type="button"
            onClick={() => { setShowCreateForm((v) => !v); setFormError(''); setForm(EMPTY_FORM); }}
            className={btnPrimary}
          >
            {showCreateForm ? 'Cancel' : '+ Add Quota'}
          </button>
        )}
      </div>

      {showCreateForm && (
        <form
          onSubmit={handleCreate}
          className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3"
        >
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Market Share Quota</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className={labelClass}>
              Territory ID <span className="text-[rgb(var(--color-danger))]">*</span>
              <input type="text" value={form.territory_id} onChange={(e) => setForm((f) => ({ ...f, territory_id: e.target.value }))} placeholder="e.g. TERR-001" className={inputClass} required />
            </label>
            <div />
            <label className={labelClass}>
              Target Share % <span className="text-[rgb(var(--color-danger))]">*</span>
              <input type="number" step="0.01" min="0" max="100" value={form.target_share_pct} onChange={(e) => setForm((f) => ({ ...f, target_share_pct: e.target.value }))} placeholder="0.00" className={inputClass} required />
            </label>
            <label className={labelClass}>
              Payout Rate % <span className="text-[rgb(var(--color-danger))]">*</span>
              <input type="number" step="0.01" min="0" value={form.payout_rate_pct} onChange={(e) => setForm((f) => ({ ...f, payout_rate_pct: e.target.value }))} placeholder="0.00" className={inputClass} required />
            </label>
            <label className={labelClass}>
              Effective Date
              <input type="date" value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} className={inputClass} />
            </label>
            <label className={labelClass}>
              Expiration Date
              <input type="date" value={form.expiration_date} onChange={(e) => setForm((f) => ({ ...f, expiration_date: e.target.value }))} className={inputClass} />
            </label>
          </div>
          {formError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{formError}</div>
          )}
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Create Quota'}</button>
            <button type="button" onClick={() => { setShowCreateForm(false); setFormError(''); }} className={btnSecondary}>Cancel</button>
          </div>
        </form>
      )}

      {listLoading && (
        <div className="animate-pulse space-y-2">
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
        </div>
      )}
      {listError && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{listError}</div>
      )}
      {!listLoading && !listError && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No market share quotas</p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Add quotas to configure market share targets by territory.</p>
        </div>
      )}
      {items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th className={thClass}>Territory</th>
                <th className={thClass}>Target Share</th>
                <th className={thClass}>Payout Rate</th>
                <th className={thClass}>Effective</th>
                <th className={thClass}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.market_share_quota_id}
                  className="cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
                  onClick={() => { setSelectedItem(item); setActionError(''); }}
                >
                  <td className={tdClass + ' font-medium'}>{item.territory_id}</td>
                  <td className={tdClass}>{item.target_share_pct != null ? `${item.target_share_pct}%` : '—'}</td>
                  <td className={tdClass}>{item.payout_rate_pct != null ? `${item.payout_rate_pct}%` : '—'}</td>
                  <td className={tdClass}>{item.effective_date ?? '—'}</td>
                  <td className={tdClass}><StatusPill status={deriveState(item)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg">
          {toast}
        </div>
      )}
    </section>
  );
}
